import { useCallback, useEffect, useMemo, useState } from "react";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../../../helpers/firebaseConfig";

export const PODCAST_FILES = [
  { key: "scriptPath", name: "script.txt" },
  { key: "outlinePath", name: "script_outline.txt" },
  { key: "mp3Path", name: "script_audio.mp3" },
];

export function podcastFilePath(builderId, run, file) {
  if (file.key === "scriptPath") {
    return String(run?.inputLocation || run?.input_location ||
      (builderId && run?.id ? `Podcasts/${builderId}/EN/builder/audio-generated/inputs/${run.id}/script.txt` : "")).trim();
  }
  const recorded = String(run?.[file.key] || "").trim();
  if (recorded) return recorded;
  const folder = String(run?.outputFolder || run?.output_folder ||
    (builderId && run?.id ? `Podcasts/${builderId}/EN/builder/audio-generated/runs/${run.id}` : ""))
    .replace(/\/+$/, "");
  return folder ? `${folder}/${file.name}` : "";
}

export default function useBuilderPodcastFiles(builderId, runs) {
  const [records, setRecords] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  const manifest = useMemo(() => runs.flatMap((run) => PODCAST_FILES.map((file) => ({
    runId: run.id,
    key: file.key,
    path: podcastFilePath(builderId, run, file),
  }))), [builderId, runs]);

  const urls = useMemo(() => {
    const result = {};
    manifest.forEach(({ runId, key, path }) => {
      const record = records[`${runId}:${key}`];
      if (record?.path !== path || !record.url) return;
      if (!result[runId]) result[runId] = {};
      result[runId][key] = record.url;
    });
    return result;
  }, [manifest, records]);

  useEffect(() => {
    const pending = manifest.filter(({ runId, key, path }) => {
      if (!path) return false;
      const record = records[`${runId}:${key}`];
      return record?.path !== path || !record.url || record.refreshKey !== refreshKey;
    });
    if (!pending.length) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all(pending.map(async (item) => {
      try {
        return { ...item, url: await getDownloadURL(storageRef(storage, item.path)), error: "" };
      } catch (checkError) {
        return {
          ...item,
          url: "",
          error: checkError?.code === "storage/object-not-found" ? "" : String(checkError?.message || checkError),
        };
      }
    })).then((checked) => {
      if (cancelled) return;
      setRecords((previous) => {
        const next = { ...previous };
        checked.forEach((item) => {
          next[`${item.runId}:${item.key}`] = { path: item.path, url: item.url, refreshKey };
        });
        return next;
      });
      setError(checked.find((item) => item.error)?.error || "");
      setLoading(false);
    });

    return () => { cancelled = true; };
    // Records change when checks complete; the next check waits for a run update or manual refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, refreshKey]);

  return { urls, loading, error, refresh };
}
